import { posKeyXYZ } from '@/voxelMath'

// Writer-based rectangle/box iterator (single pass, no intermediate Map)
export function buildRectVoxelsWrite(
  min: { x: number; y: number; z: number },
  max: { x: number; y: number; z: number },
  placement: 'filled' | 'outline',
  write: (key: number, x: number, y: number, z: number) => void
): void {
  const lo = { x: Math.min(min.x, max.x), y: Math.min(min.y, max.y), z: Math.min(min.z, max.z) }
  const hi = { x: Math.max(min.x, max.x), y: Math.max(min.y, max.y), z: Math.max(min.z, max.z) }

  if (placement === 'filled') {
    for (let x = lo.x; x <= hi.x; x++) {
      for (let y = lo.y; y <= hi.y; y++) {
        for (let z = lo.z; z <= hi.z; z++) {
          const k = posKeyXYZ(x, y, z)
          write(k, x, y, z)
        }
      }
    }
    return
  }

  // Outline/edges only
  const dx = hi.x - lo.x
  const dy = hi.y - lo.y
  const dz = hi.z - lo.z
  const varying = { x: dx > 0, y: dy > 0, z: dz > 0 }
  const varyingCount = (varying.x ? 1 : 0) + (varying.y ? 1 : 0) + (varying.z ? 1 : 0)

  const add = (x: number, y: number, z: number) => {
    const k = posKeyXYZ(x, y, z)
    write(k, x, y, z)
  }
  const lineX = (y: number, z: number) => {
    for (let x = lo.x; x <= hi.x; x++) add(x, y, z)
  }
  const lineY = (x: number, z: number) => {
    for (let y = lo.y; y <= hi.y; y++) add(x, y, z)
  }
  const lineZ = (x: number, y: number) => {
    for (let z = lo.z; z <= hi.z; z++) add(x, y, z)
  }

  if (varyingCount <= 1) {
    if (varying.x) lineX(lo.y, lo.z)
    else if (varying.y) lineY(lo.x, lo.z)
    else if (varying.z) lineZ(lo.x, lo.y)
    else add(lo.x, lo.y, lo.z)
    return
  }

  if (varyingCount === 2) {
    if (!varying.x) {
      const xf = lo.x
      lineY(xf, lo.z)
      lineY(xf, hi.z)
      lineZ(xf, lo.y)
      lineZ(xf, hi.y)
    } else if (!varying.y) {
      const yf = lo.y
      lineX(lo.y, lo.z)
      lineX(lo.y, hi.z)
      lineZ(lo.x, yf)
      lineZ(hi.x, yf)
    } else {
      const zf = lo.z
      lineX(lo.y, zf)
      lineX(hi.y, zf)
      lineY(lo.x, zf)
      lineY(hi.x, zf)
    }
    return
  }

  for (const y of [lo.y, hi.y]) {
    for (const z of [lo.z, hi.z]) lineX(y, z)
  }
  for (const x of [lo.x, hi.x]) {
    for (const z of [lo.z, hi.z]) lineY(x, z)
  }
  for (const x of [lo.x, hi.x]) {
    for (const y of [lo.y, hi.y]) lineZ(x, y)
  }
}
// Efficient iterator: visit each rectangle key without allocating a Map
export function forEachRectKey(
  min: { x: number; y: number; z: number },
  max: { x: number; y: number; z: number },
  placement: 'filled' | 'outline',
  visit: (key: number, x: number, y: number, z: number) => void
): void {
  const lo = { x: Math.min(min.x, max.x), y: Math.min(min.y, max.y), z: Math.min(min.z, max.z) }
  const hi = { x: Math.max(min.x, max.x), y: Math.max(min.y, max.y), z: Math.max(min.z, max.z) }

  if (placement === 'filled') {
    for (let x = lo.x; x <= hi.x; x++) {
      for (let y = lo.y; y <= hi.y; y++) {
        for (let z = lo.z; z <= hi.z; z++) {
          const k = posKeyXYZ(x, y, z)
          visit(k, x, y, z)
        }
      }
    }
    return
  }

  // Outline/edges only
  const dx = hi.x - lo.x
  const dy = hi.y - lo.y
  const dz = hi.z - lo.z
  const varying = { x: dx > 0, y: dy > 0, z: dz > 0 }
  const varyingCount = (varying.x ? 1 : 0) + (varying.y ? 1 : 0) + (varying.z ? 1 : 0)

  const add = (x: number, y: number, z: number) => {
    const k = posKeyXYZ(x, y, z)
    visit(k, x, y, z)
  }
  const lineX = (y: number, z: number) => {
    for (let x = lo.x; x <= hi.x; x++) add(x, y, z)
  }
  const lineY = (x: number, z: number) => {
    for (let y = lo.y; y <= hi.y; y++) add(x, y, z)
  }
  const lineZ = (x: number, y: number) => {
    for (let z = lo.z; z <= hi.z; z++) add(x, y, z)
  }

  if (varyingCount <= 1) {
    if (varying.x) lineX(lo.y, lo.z)
    else if (varying.y) lineY(lo.x, lo.z)
    else if (varying.z) lineZ(lo.x, lo.y)
    else add(lo.x, lo.y, lo.z)
    return
  }

  if (varyingCount === 2) {
    if (!varying.x) {
      const xf = lo.x
      lineY(xf, lo.z)
      lineY(xf, hi.z)
      lineZ(xf, lo.y)
      lineZ(xf, hi.y)
    } else if (!varying.y) {
      const yf = lo.y
      lineX(lo.y, lo.z)
      lineX(lo.y, hi.z)
      lineZ(lo.x, yf)
      lineZ(hi.x, yf)
    } else {
      const zf = lo.z
      lineX(lo.y, zf)
      lineX(hi.y, zf)
      lineY(lo.x, zf)
      lineY(hi.x, zf)
    }
    return
  }

  for (const y of [lo.y, hi.y]) {
    for (const z of [lo.z, hi.z]) lineX(y, z)
  }
  for (const x of [lo.x, hi.x]) {
    for (const z of [lo.z, hi.z]) lineY(x, z)
  }
  for (const x of [lo.x, hi.x]) {
    for (const y of [lo.y, hi.y]) lineZ(x, y)
  }
}
